/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { v4 as uuidv4 } from 'uuid';

// Misc imports
import { ANALYTIC_TAGS_RECOMMENDATIONS, GRAPH_LAYOUTS, NODE_TYPES } from '../../../constants/flowGraph';
import { ACTIVITY_TYPES, CHAPTERSOURCE_SCHEMAS } from '../../../constants/main';
import { generateRandomString } from '../../string/generateRandomString';

/**
 * Generate a conversation flow graph that contains the bare minimum to pass the validation.
 *
 * This includes:
 * - An intro node
 * - A question node
 * - Two option nodes for the previous question
 * - A message node
 *
 * @returns {Object} The generated graph.
 */
const generateConversationFlowGraph = (chaptersourceDisplayName, primaryBotLanguage, chapterSettings) => {
  const introNodeId = generateRandomString(6);
  const questionNodeId = uuidv4();
  const option1NodeId = uuidv4();
  const option2NodeId = uuidv4();
  const messageNodeId = uuidv4();

  const graph = {
    id: uuidv4(),
    name: {
      [primaryBotLanguage]: chaptersourceDisplayName
    },
    schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
    group: {
      [primaryBotLanguage]: 'Default'
    },
    content: {
      schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
      layout: GRAPH_LAYOUTS.VERTICAL,
      nodes: [
        {
          id: introNodeId,
          type: NODE_TYPES.INTRO,
          position: {
            x: 100,
            y: 0
          },
          label: {
            [primaryBotLanguage]: ''
          }
        },
        {
          id: questionNodeId,
          type: NODE_TYPES.SELECT,
          position: {
            x: 100,
            y: 100
          },
          label: {
            [primaryBotLanguage]: 'Do you like agents?'
          },
          enableBackAction: true,
          isDropdown: false,
          questionId: generateRandomString(6)
        },
        {
          id: option1NodeId,
          type: NODE_TYPES.OPTION,
          position: {
            x: 0,
            y: 220
          },
          label: {
            [primaryBotLanguage]: 'Yes, I do!'
          },
          priority: 1
        },
        {
          id: option2NodeId,
          type: NODE_TYPES.OPTION,
          position: {
            x: 210,
            y: 220
          },
          label: {
            [primaryBotLanguage]: 'I love them!'
          },
          priority: 2
        },
        {
          id: messageNodeId,
          type: NODE_TYPES.MESSAGE,
          position: {
            x: 100,
            y: 330
          },
          label: {
            [primaryBotLanguage]: 'We love them too! :)'
          }
        }
      ],
      edges: [
        {
          // Connecting intro node with first question
          id: uuidv4(),
          source: introNodeId,
          target: questionNodeId,
          label: ''
        },
        {
          // Connecting question to option 1
          id: uuidv4(),
          source: questionNodeId,
          target: option1NodeId,
          label: ''
        },
        {
          // Connecting question to option 2
          id: uuidv4(),
          source: questionNodeId,
          target: option2NodeId,
          label: ''
        },
        {
          // Connecting option 1 to final message
          id: uuidv4(),
          source: option1NodeId,
          target: messageNodeId,
          label: ''
        },
        {
          // Connecting option 2 to final message
          id: uuidv4(),
          source: option2NodeId,
          target: messageNodeId,
          label: ''
        }
      ]
    }
  };

  if (chapterSettings) {
    graph.content.settings = chapterSettings;
  }

  return graph;
};

const generateFeedbackFlowGraph = (chaptersourceDisplayName, primaryBotLanguage) => {
  const introNodeId = generateRandomString(6);
  const questionNodeId = uuidv4();
  const option1NodeId = uuidv4();
  const option2NodeId = uuidv4();
  const analytics1NodeId = uuidv4();
  const analytics2NodeId = uuidv4();
  const messageNodeId = uuidv4();

  const graph = {
    id: uuidv4(),
    name: {
      [primaryBotLanguage]: chaptersourceDisplayName
    },
    schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
    group: {
      [primaryBotLanguage]: 'Default'
    },
    content: {
      schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
      layout: GRAPH_LAYOUTS.VERTICAL,
      nodes: [
        {
          id: introNodeId,
          type: NODE_TYPES.INTRO,
          position: {
            x: 210,
            y: 0
          },
          label: {
            [primaryBotLanguage]: ''
          }
        },
        {
          id: questionNodeId,
          type: NODE_TYPES.SELECT,
          position: {
            x: 210,
            y: 100
          },
          label: {
            [primaryBotLanguage]: 'How did you find this response?'
          },
          enableBackAction: true,
          isDropdown: false,
          questionId: generateRandomString(6)
        },
        {
          id: option1NodeId,
          type: NODE_TYPES.OPTION,
          position: {
            x: 100,
            y: 240
          },
          label: {
            [primaryBotLanguage]: 'Poor'
          },
          priority: 1
        },
        {
          id: option2NodeId,
          type: NODE_TYPES.OPTION,
          position: {
            x: 330,
            y: 240
          },
          label: {
            [primaryBotLanguage]: 'Excellent'
          },
          priority: 2
        },
        {
          id: analytics1NodeId,
          type: NODE_TYPES.ANALYTIC_TAGS,
          position: {
            x: 100,
            y: 350
          },
          label: {
            [primaryBotLanguage]: ''
          },
          analyticTags: [ANALYTIC_TAGS_RECOMMENDATIONS.USER_FEEDBACK_NEGATIVE]
        },
        {
          id: analytics2NodeId,
          type: NODE_TYPES.ANALYTIC_TAGS,
          position: {
            x: 330,
            y: 350
          },
          label: {
            [primaryBotLanguage]: ''
          },
          analyticTags: [ANALYTIC_TAGS_RECOMMENDATIONS.USER_FEEDBACK_POSITIVE]
        },
        {
          id: messageNodeId,
          type: NODE_TYPES.MESSAGE,
          position: {
            x: 210,
            y: 470
          },
          label: {
            [primaryBotLanguage]: 'Thank you.'
          }
        }
      ],
      edges: [
        {
          // Connecting intro node with first question
          id: uuidv4(),
          source: introNodeId,
          target: questionNodeId,
          label: ''
        },
        {
          // Connecting question to option 1
          id: uuidv4(),
          source: questionNodeId,
          target: option1NodeId,
          label: ''
        },
        {
          // Connecting question to option 2
          id: uuidv4(),
          source: questionNodeId,
          target: option2NodeId,
          label: ''
        },
        {
          // Connecting option 1 to analytics tags 1
          id: uuidv4(),
          source: option1NodeId,
          target: analytics1NodeId,
          label: ''
        },
        {
          // Connecting option 2 to analytics tags 3
          id: uuidv4(),
          source: option2NodeId,
          target: analytics2NodeId,
          label: ''
        },

        {
          // Connecting analytics tags 1 to final message
          id: uuidv4(),
          source: analytics1NodeId,
          target: messageNodeId,
          label: ''
        },
        {
          // Connecting analytics tags 2 to final message
          id: uuidv4(),
          source: analytics2NodeId,
          target: messageNodeId,
          label: ''
        }
      ]
    }
  };

  return graph;
};

/**
 * Generate a live-chat flow graph that contains the bare minimum to pass the validation.
 *
 * This includes:
 * - An intro node
 * - A question node
 * - Two option nodes for the previous question
 * - Two message nodes (one for each option)
 * - A live-chat node
 *
 * @returns {Object} The generated graph.
 */
const generateLivechatFlowGraph = (chaptersourceDisplayName, primaryBotLanguage) => {
  const introNodeId = generateRandomString(6);
  const questionNodeId = uuidv4();
  const option1NodeId = uuidv4();
  const option2NodeId = uuidv4();
  const messageNode1Id = uuidv4();
  const messageNode2Id = uuidv4();
  const livechatNodeId = uuidv4();

  const graph = {
    id: uuidv4(),
    name: {
      [primaryBotLanguage]: chaptersourceDisplayName
    },
    schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
    group: {
      [primaryBotLanguage]: 'Default'
    },
    content: {
      schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
      layout: GRAPH_LAYOUTS.VERTICAL,
      nodes: [
        {
          id: introNodeId,
          type: NODE_TYPES.INTRO,
          position: {
            x: 100,
            y: 0
          },
          label: {
            [primaryBotLanguage]: ''
          }
        },
        {
          id: questionNodeId,
          type: NODE_TYPES.SELECT,
          position: {
            x: 100,
            y: 100
          },
          label: {
            [primaryBotLanguage]: 'Do you want to forward your request to a live agent?'
          },
          enableBackAction: true,
          isDropdown: false,
          questionId: generateRandomString(6)
        },
        {
          id: option1NodeId,
          type: NODE_TYPES.OPTION,
          position: {
            x: 0,
            y: 280
          },
          label: {
            [primaryBotLanguage]: 'Yes'
          },
          priority: 1
        },
        {
          id: option2NodeId,
          type: NODE_TYPES.OPTION,
          position: {
            x: 210,
            y: 280
          },
          label: {
            [primaryBotLanguage]: 'No'
          },
          priority: 2
        },
        {
          id: messageNode1Id,
          type: NODE_TYPES.MESSAGE,
          position: {
            x: 0,
            y: 390
          },
          label: {
            [primaryBotLanguage]: 'Great! I am forwarding your live-chat request.'
          }
        },
        {
          id: livechatNodeId,
          type: NODE_TYPES.LIVECHAT_REQUEST,
          position: {
            x: 0,
            y: 550
          },
          label: {
            [primaryBotLanguage]: ''
          }
        },
        {
          id: messageNode2Id,
          type: NODE_TYPES.MESSAGE,
          position: {
            x: 210,
            y: 410
          },
          label: {
            [primaryBotLanguage]: 'OK'
          }
        }
      ],
      edges: [
        {
          // Connecting intro node with first question
          id: uuidv4(),
          source: introNodeId,
          target: questionNodeId,
          label: ''
        },
        {
          // Connecting question to option 1
          id: uuidv4(),
          source: questionNodeId,
          target: option1NodeId,
          label: ''
        },
        {
          // Connecting question to option 2
          id: uuidv4(),
          source: questionNodeId,
          target: option2NodeId,
          label: ''
        },
        {
          // Connecting option 1 to message 1 node
          id: uuidv4(),
          source: option1NodeId,
          target: messageNode1Id,
          label: ''
        },
        {
          // Connecting option 2 to message 2 node
          id: uuidv4(),
          source: option2NodeId,
          target: messageNode2Id,
          label: ''
        },
        {
          // Connecting message 1 to live-chat node
          id: uuidv4(),
          source: messageNode1Id,
          target: livechatNodeId,
          label: ''
        }
      ]
    }
  };

  return graph;
};

/**
 * Generate a live-chat flow graph that contains the bare minimum to pass the validation.
 *
 * @param {String} name The display name of the activity.
 * @param {String} type The activity type of the chaptersource.
 *
 * @returns {Object} The generated graph.
 */
export const generateFlowGraphChaptersource = (name, type, primaryBotLanguage) => {
  switch (type) {
    case ACTIVITY_TYPES.DECISION_TREE:
      return generateConversationFlowGraph(name, primaryBotLanguage);

    case ACTIVITY_TYPES.INTERVIEW:
      return generateConversationFlowGraph(name, primaryBotLanguage, { storeAnswersByDefault: true });

    case ACTIVITY_TYPES.LIVECHAT_FLOW:
      return generateLivechatFlowGraph(name, primaryBotLanguage);

    case ACTIVITY_TYPES.USER_FEEDBACK:
      return generateFeedbackFlowGraph(name, primaryBotLanguage);

    default:
      break;
  }
};

export const generateNlpFlowGraph = (name, language) => {
  const introNodeId = generateRandomString(6);
  const queryNodeId = uuidv4();
  const pipeline1NodeId = uuidv4();
  const pipeline2NodeId = uuidv4();

  const graph = {
    name: name,
    language: language,
    nodes: [
      {
        id: introNodeId,
        type: NODE_TYPES.INTRO,
        position: {
          x: 100,
          y: 0
        },
        label: {}
      },
      {
        id: queryNodeId,
        type: NODE_TYPES.QUERY,
        position: {
          x: 100,
          y: 100
        },
        label: {},
        query: null
      },
      {
        id: pipeline1NodeId,
        type: NODE_TYPES.PIPELINE,
        position: {
          x: 0,
          y: 250
        },
        label: {},
        pipeline: null
      },
      {
        id: pipeline2NodeId,
        type: NODE_TYPES.PIPELINE,
        position: {
          x: 210,
          y: 250
        },
        label: {},
        pipeline: null
      }
    ],
    edges: [
      {
        // Connecting intro node with query
        id: uuidv4(),
        source: introNodeId,
        target: queryNodeId,
        label: ''
      },
      {
        // Connecting query to pipeline 1
        id: uuidv4(),
        source: queryNodeId,
        target: pipeline1NodeId,
        label: 'true'
      },
      {
        // Connecting query to pipeline 2
        id: uuidv4(),
        source: queryNodeId,
        target: pipeline2NodeId,
        label: 'false'
      }
    ]
  };

  return graph;
};
